import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Store, Loader2, Plus, Tag, History, ArrowRight } from 'lucide-react';

const inr = (v) => `₹${(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const feeStr = (pct, fixed) => `${(Number(pct) || 0)}% + ₹${(Number(fixed) || 0)}`;

export default function ResellerMerchantsPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [priceFor, setPriceFor] = useState(null);
  const [pf, setPf] = useState({ pct: '', fixed: '' });
  const [confirmStep, setConfirmStep] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ business_name: '', contact_name: '', email: '', phone: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get('/reseller/merchants'); setData(r.data); } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const openPrice = (m) => {
    setPriceFor(m);
    setConfirmStep(false);
    setPf({ pct: String(m.pricing?.merchant_percentage_fee ?? data.base_percentage),
            fixed: String(m.pricing?.merchant_fixed_fee ?? data.base_fixed) });
  };

  // Live margin/commission preview on ₹10,000
  const previewCommission = () => {
    const pct = parseFloat(pf.pct) || 0, fixed = parseFloat(pf.fixed) || 0;
    const merch = 10000 * pct / 100 + fixed;
    const base = 10000 * data.base_percentage / 100 + data.base_fixed;
    return { merch: merch.toFixed(2), base: base.toFixed(2), commission: Math.max(0, merch - base).toFixed(2) };
  };

  const savePrice = async () => {
    setBusy(true);
    try {
      await api.put('/reseller/pricing', { merchant_id: priceFor.merchant.id,
        merchant_percentage_fee: parseFloat(pf.pct) || 0, merchant_fixed_fee: parseFloat(pf.fixed) || 0 });
      toast.success('Pricing updated'); setPriceFor(null); setConfirmStep(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  const openHistory = async (m) => {
    setHistoryFor(m); setHistory(null);
    try { const r = await api.get(`/reseller/merchants/${m.merchant.id}/pricing-history`); setHistory(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load history'); }
  };

  const addMerchant = async () => {
    setBusy(true);
    try {
      const r = await api.post('/reseller/merchants', addForm);
      toast.success(r.data.message); setAddOpen(false);
      setAddForm({ business_name: '', contact_name: '', email: '', phone: '', password: '' }); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  if (loading || !data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const pv = priceFor ? previewCommission() : null;

  return (
    <div className="space-y-6" data-testid="reseller-merchants-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><Store className="w-6 h-6" />My Merchants</h1>
          <p className="text-muted-foreground">AwadhPay base: {data.base_percentage}% + {inr(data.base_fixed)} · max margin {data.max_margin}%</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-merchant-btn"><Plus className="w-4 h-4 mr-2" />Add Merchant</Button>
      </div>

      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
            <tr><th className="px-4 py-3">Merchant</th><th className="px-4 py-3">KYC</th><th className="px-4 py-3">Current Fee</th><th className="px-4 py-3">Commission</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {data.merchants.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No merchants yet. Add one to get started.</td></tr>}
            {data.merchants.map((m) => (
              <tr key={m.merchant.id} className="border-t" data-testid={`rm-row-${m.merchant.id}`}>
                <td className="px-4 py-3"><div className="font-medium">{m.merchant.business_name}</div><div className="text-xs text-muted-foreground">{m.merchant.email}</div></td>
                <td className="px-4 py-3"><Badge variant={m.merchant.kyc_status === 'approved' ? 'default' : 'secondary'}>{m.merchant.kyc_status}</Badge></td>
                <td className="px-4 py-3 font-mono">{m.pricing ? `${m.pricing.merchant_percentage_fee}% + ₹${m.pricing.merchant_fixed_fee}` : 'base'}</td>
                <td className="px-4 py-3 font-mono">{inr(m.commission)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => openHistory(m)} data-testid={`history-${m.merchant.id}`}><History className="w-4 h-4 mr-1" />History</Button>
                    <Button size="sm" variant="outline" disabled={!data.pricing_enabled} onClick={() => openPrice(m)} data-testid={`set-pricing-${m.merchant.id}`}><Tag className="w-4 h-4 mr-1" />Pricing</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>

      {/* Pricing dialog — two-step: edit -> confirm */}
      <Dialog open={!!priceFor} onOpenChange={(o) => { if (!o) { setPriceFor(null); setConfirmStep(false); } }}>
        <DialogContent data-testid="pricing-dialog">
          {!confirmStep ? (
            <>
              <DialogHeader><DialogTitle>Set Merchant Pricing</DialogTitle><DialogDescription>{priceFor?.merchant?.business_name} · base {data.base_percentage}% + ₹{data.base_fixed}</DialogDescription></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Merchant %</Label><Input type="number" step="0.01" value={pf.pct} onChange={(e) => setPf({ ...pf, pct: e.target.value })} data-testid="pricing-pct" /></div>
                <div><Label>Fixed ₹</Label><Input type="number" step="0.01" value={pf.fixed} onChange={(e) => setPf({ ...pf, fixed: e.target.value })} data-testid="pricing-fixed" /></div>
              </div>
              {pv && (
                <div className="rounded-lg border divide-y text-sm mt-2" data-testid="pricing-preview">
                  <div className="flex justify-between px-4 py-2"><span className="text-muted-foreground">On ₹10,000 — Merchant Fee</span><span className="font-mono">₹{pv.merch}</span></div>
                  <div className="flex justify-between px-4 py-2"><span className="text-muted-foreground">AwadhPay Base</span><span className="font-mono">₹{pv.base}</span></div>
                  <div className="flex justify-between px-4 py-2 bg-muted/40"><span className="font-semibold">Your Commission</span><span className="font-mono font-bold text-green-600">₹{pv.commission}</span></div>
                </div>
              )}
              <DialogFooter><Button onClick={() => setConfirmStep(true)} data-testid="review-pricing-btn">Review Change</Button></DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader><DialogTitle>Confirm Pricing Change</DialogTitle><DialogDescription>Review before applying to {priceFor?.merchant?.business_name}.</DialogDescription></DialogHeader>
              <div className="rounded-lg border divide-y text-sm mt-1" data-testid="pricing-confirm">
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-muted-foreground">Merchant Fee</span>
                  <span className="flex items-center gap-2 font-mono">
                    <span className="text-muted-foreground line-through">{feeStr(priceFor?.pricing?.merchant_percentage_fee ?? data.base_percentage, priceFor?.pricing?.merchant_fixed_fee ?? data.base_fixed)}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="font-semibold text-foreground" data-testid="confirm-new-fee">{feeStr(pf.pct, pf.fixed)}</span>
                  </span>
                </div>
                <div className="flex justify-between px-4 py-3"><span className="text-muted-foreground">AwadhPay Base Fee</span><span className="font-mono">{feeStr(data.base_percentage, data.base_fixed)}</span></div>
                <div className="flex justify-between px-4 py-3 bg-muted/40">
                  <span className="font-semibold">Estimated Reseller Margin</span>
                  <span className="font-mono font-bold text-green-600" data-testid="confirm-margin">{Math.max(0, (parseFloat(pf.pct) || 0) - data.base_percentage).toFixed(2)}% + ₹{Math.max(0, (parseFloat(pf.fixed) || 0) - data.base_fixed).toFixed(2)}</span>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmStep(false)} data-testid="back-pricing-btn">Back</Button>
                <Button onClick={savePrice} disabled={busy} data-testid="save-pricing-btn">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Confirm &amp; Apply</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Pricing history dialog */}
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent className="max-w-2xl" data-testid="pricing-history-dialog">
          <DialogHeader>
            <DialogTitle>Pricing History</DialogTitle>
            <DialogDescription>{historyFor?.merchant?.business_name} · current base {data.base_percentage}% + ₹{data.base_fixed}</DialogDescription>
          </DialogHeader>
          {!history ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div> : (
            <div className="max-h-[55vh] overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs text-muted-foreground sticky top-0">
                  <tr><th className="px-3 py-2">Effective</th><th className="px-3 py-2">Merchant Fee</th><th className="px-3 py-2">Base Fee</th><th className="px-3 py-2">Reseller Margin</th></tr>
                </thead>
                <tbody>
                  {history.history.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">No pricing set yet — merchant uses base fee.</td></tr>}
                  {history.history.map((h, i) => (
                    <tr key={i} className="border-t" data-testid={`history-row-${i}`}>
                      <td className="px-3 py-2 text-xs">{new Date(h.effective_from).toLocaleString('en-IN')}{i === 0 && <Badge variant="outline" className="ml-2 rounded-sm text-[9px] bg-green-500/15 text-green-600 border-green-500/30">CURRENT</Badge>}</td>
                      <td className="px-3 py-2 font-mono">{feeStr(h.merchant_percentage_fee, h.merchant_fixed_fee)}</td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">{feeStr(h.base_percentage, h.base_fixed)}</td>
                      <td className="px-3 py-2 font-mono text-green-600">{h.reseller_margin_percentage}% + ₹{h.reseller_margin_fixed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setHistoryFor(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add merchant dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Merchant</DialogTitle><DialogDescription>Merchant will require Admin KYC approval before going live.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {[['business_name', 'Business Name'], ['contact_name', 'Contact Name'], ['email', 'Email'], ['phone', 'Phone'], ['password', 'Temp Password']].map(([k, l]) => (
              <div key={k}><Label>{l}</Label><Input type={k === 'password' ? 'text' : 'text'} value={addForm[k]} onChange={(e) => setAddForm({ ...addForm, [k]: e.target.value })} data-testid={`add-${k}`} /></div>
            ))}
          </div>
          <DialogFooter><Button onClick={addMerchant} disabled={busy} data-testid="submit-add-merchant">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Onboard Merchant</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
